/** Renderer-only drafts. No keystroke mutates a document or becomes ground truth. */
export type FieldDraftBinding = {
  documentId: string;
  fieldId: string;
  name: string;
  documentMutationToken: string;
  fieldContractToken: string;
  pageNumber: number;
  baseValue: string;
};
export type FieldDraft = FieldDraftBinding & {
  id: string;
  revision: number;
  value: string;
  state: 'editing' | 'saving' | 'refused';
  reason?: string;
};
export type FieldDraftIdentity = Pick<FieldDraft, 'id' | 'revision' | 'documentId' | 'fieldId'>;
export type FieldDraftOutcome =
  { status: 'saved'; value: string } | { status: 'refused'; reason: string };
export type FieldDraftCommand =
  | { type: 'edit' | 'replace'; draft: FieldDraft }
  | { type: 'submit' | 'discard'; draft: FieldDraftIdentity };
export type FieldDraftEvent =
  | { type: 'field-draft-sync'; draft: FieldDraft }
  | { type: 'field-draft-result'; draft: FieldDraftIdentity; outcome: FieldDraftOutcome }
  | { type: 'field-draft-discarded'; draft: FieldDraftIdentity };
export const fieldDraftKey = (binding: Pick<FieldDraftBinding, 'documentId' | 'fieldId'>) =>
  JSON.stringify([binding.documentId, binding.fieldId]);
export const FIELD_DRAFT_LIMIT = 32;
export const FIELD_DRAFT_TEXT_LIMIT = 32768;

/** Same state machine in both surfaces; the owner retains it when a panel closes. */
export class FieldDraftStore {
  private entries = new Map<string, FieldDraft>();
  private snapshot: readonly FieldDraft[] = [];
  private listeners = new Set<() => void>();
  private outcomes = new Set<(identity: FieldDraftIdentity, outcome: FieldDraftOutcome) => void>();
  private retired = new Set<string>();
  /** The revision each draft was rebased from: typing still arriving on it from another surface follows it. */
  private rebasedFrom = new Map<
    string,
    Pick<FieldDraft, 'documentMutationToken' | 'fieldContractToken' | 'revision'>
  >();
  private publish: (command: FieldDraftCommand) => void;
  private readonly canEdit: () => boolean;
  constructor(
    publish: (command: FieldDraftCommand) => void = () => {},
    canEdit: () => boolean = () => true,
  ) {
    this.publish = publish;
    this.canEdit = canEdit;
  }
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  getSnapshot = () => this.snapshot;
  subscribeOutcome = (
    listener: (identity: FieldDraftIdentity, outcome: FieldDraftOutcome) => void,
  ) => {
    this.outcomes.add(listener);
    return () => {
      this.outcomes.delete(listener);
    };
  };
  get = (binding: Pick<FieldDraftBinding, 'documentId' | 'fieldId'>) =>
    this.entries.get(fieldDraftKey(binding));
  private changed() {
    this.snapshot = [...this.entries.values()];
    for (const listener of this.listeners) listener();
  }
  private matches(identity: FieldDraftIdentity) {
    const draft = this.get(identity);
    return draft?.id === identity.id && draft.revision === identity.revision ? draft : undefined;
  }
  edit(binding: FieldDraftBinding, value: string) {
    if (!this.canEdit()) return 'Paige is checking whether it can close. Your typing is kept.';
    const prior = this.get(binding);
    if (prior?.state === 'saving') return 'Wait for this value to finish saving.';
    if (!prior && this.entries.size >= FIELD_DRAFT_LIMIT)
      return 'Finish saving or discard an unsaved field before editing another. Your existing drafts are kept.';
    if (value.length > FIELD_DRAFT_TEXT_LIMIT)
      return 'This field is too long to edit here. Your previous typing is kept.';
    // Typing again into a refused draft (its field changed under it) is the person choosing their value over
    // the one shown beside it, as "Save my value" is: the draft takes the field as it is now and saves normally.
    // Before, it stayed refused and nothing typed afterwards was ever saved (6.2.57 check).
    const draft: FieldDraft =
      prior?.state === 'refused'
        ? { ...binding, id: prior.id, revision: prior.revision + 1, value, state: 'editing' }
        : prior
          ? { ...prior, revision: prior.revision + 1, value }
          : { ...binding, id: crypto.randomUUID(), revision: 1, value, state: 'editing' };
    this.entries.set(fieldDraftKey(draft), draft);
    this.changed();
    // A rebound refused draft carries the field's newer tokens: the owner takes it as the explicit choice it is.
    this.publish({ type: prior?.state === 'refused' ? 'replace' : 'edit', draft });
  }
  /** Only an explicit conflict choice can bind old typing to a newer revision. */
  replace(binding: FieldDraftBinding) {
    if (!this.canEdit()) return;
    const previous = this.get(binding);
    if (!previous || previous.state === 'saving') return;
    const draft: FieldDraft = {
      ...binding,
      id: previous.id,
      revision: previous.revision + 1,
      value: previous.value,
      state: 'editing',
    };
    this.entries.set(fieldDraftKey(draft), draft);
    this.changed();
    this.publish({ type: 'replace', draft });
    this.submit(draft);
  }
  /**
   * The owner's own save of another cell landed under this typing and changed nothing it rests on
   * (the caller checks that): the typing moves onto the saved revision and stays unsubmitted. Sent
   * as a replacement so a detached panel holding the same typing moves with it.
   */
  rebase(binding: FieldDraftBinding) {
    const previous = this.get(binding);
    if (!previous || previous.state !== 'editing') return;
    const draft: FieldDraft = {
      ...previous,
      documentMutationToken: binding.documentMutationToken,
      fieldContractToken: binding.fieldContractToken,
      revision: previous.revision + 1,
    };
    this.rebasedFrom.set(previous.id, {
      documentMutationToken: previous.documentMutationToken,
      fieldContractToken: previous.fieldContractToken,
      revision: previous.revision,
    });
    this.entries.set(fieldDraftKey(draft), draft);
    this.changed();
    this.publish({ type: 'replace', draft });
  }
  /**
   * The draft a submit or discard from another surface names: its current revision, or the revision
   * it was just rebased from when nothing was typed since (the move is the only newer revision). A
   * panel that discarded or submitted before it heard of the move meant this same typing.
   */
  commandTarget(identity: FieldDraftIdentity) {
    const draft = this.get(identity);
    if (!draft || draft.id !== identity.id) return undefined;
    if (draft.revision === identity.revision) return draft;
    const from = this.rebasedFrom.get(draft.id);
    return from && identity.revision === from.revision && draft.revision === from.revision + 1
      ? draft
      : undefined;
  }
  submit(identity: FieldDraftIdentity | FieldDraftBinding) {
    if (!this.canEdit()) return;
    const draft = this.get(identity);
    if ('id' in identity && !this.matches(identity)) return;
    if (!draft || draft.state !== 'editing') return;
    if (draft.value === draft.baseValue) {
      this.discard(draft);
      return;
    }
    this.entries.set(fieldDraftKey(draft), { ...draft, state: 'saving' });
    this.changed();
    this.publish({ type: 'submit', draft });
  }
  discard(identity: FieldDraftIdentity) {
    if (!this.canEdit()) return;
    if (!this.matches(identity) || this.get(identity)?.state === 'saving') return;
    this.publish({ type: 'discard', draft: identity });
    this.remove(identity);
  }
  /** Owner/snapshot transport never silently replaces newer local typing. */
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  merge(draft: FieldDraft, explicitReplacement = false): boolean {
    const previous = this.get(draft);
    if (
      this.retired.has(draft.id) ||
      draft.value.length > FIELD_DRAFT_TEXT_LIMIT ||
      (!previous && this.entries.size >= FIELD_DRAFT_LIMIT)
    )
      return false;
    if (previous && previous.id !== draft.id) return false;
    // Typing from a surface that has not yet heard this draft was rebased (rebase): it is the
    // same typing on the revision it was moved from, so it moves too, as the newest revision.
    const from = previous && this.rebasedFrom.get(previous.id);
    if (
      previous &&
      from &&
      !explicitReplacement &&
      previous.state === 'editing' &&
      draft.state === 'editing' &&
      draft.revision >= previous.revision &&
      previous.name === draft.name &&
      draft.documentMutationToken === from.documentMutationToken &&
      draft.fieldContractToken === from.fieldContractToken &&
      (previous.documentMutationToken !== draft.documentMutationToken ||
        previous.fieldContractToken !== draft.fieldContractToken)
    ) {
      const moved: FieldDraft = {
        ...draft,
        documentMutationToken: previous.documentMutationToken,
        fieldContractToken: previous.fieldContractToken,
        revision: Math.max(draft.revision, previous.revision + 1),
      };
      // The move raised the revision past the one that surface typed: its discard or submit for that
      // revision, sent before it hears of the move, still means this typing (commandTarget).
      if (moved.revision === draft.revision + 1)
        this.rebasedFrom.set(previous.id, { ...from, revision: draft.revision });
      this.entries.set(fieldDraftKey(moved), moved);
      this.changed();
      return true;
    }
    if (previous && previous.revision > draft.revision) return false;
    const stateOrder = { editing: 0, saving: 1, refused: 2 };
    if (
      previous &&
      previous.revision === draft.revision &&
      stateOrder[previous.state] > stateOrder[draft.state]
    )
      return false;
    // A newer revision of exactly the text held here replaces no typing: it is the owner moving it (rebase).
    const sameTypingNewerRevision =
      previous !== undefined &&
      previous.revision < draft.revision &&
      previous.value === draft.value;
    if (
      previous &&
      !explicitReplacement &&
      (previous.name !== draft.name ||
        (!sameTypingNewerRevision &&
          (previous.documentMutationToken !== draft.documentMutationToken ||
            previous.fieldContractToken !== draft.fieldContractToken)))
    )
      return false;
    this.entries.set(fieldDraftKey(draft), draft);
    this.changed();
    return true;
  }
  finish(identity: FieldDraftIdentity, outcome: FieldDraftOutcome) {
    const draft = this.matches(identity);
    if (!draft) return;
    if (outcome.status === 'saved' && outcome.value === draft.value) {
      this.entries.delete(fieldDraftKey(draft));
      this.retire(draft.id);
    } else
      this.entries.set(fieldDraftKey(draft), {
        ...draft,
        state: 'refused',
        reason:
          outcome.status === 'refused'
            ? outcome.reason
            : 'The saved value differs. Your typing is kept below.',
      });
    this.changed();
    for (const listener of this.outcomes) listener(identity, outcome);
  }
  private retire(id: string) {
    this.retired.add(id);
    // Only completed/discarded correlation IDs expire, never unsaved typing.
    if (this.retired.size > 256) this.retired.delete(this.retired.values().next().value!);
  }
  remove(identity: FieldDraftIdentity) {
    if (!this.matches(identity)) return;
    this.entries.delete(fieldDraftKey(identity));
    this.retire(identity.id);
    this.changed();
  }
}

/** One coordinator for docked and detached commands; the native save lane stays the owner. */
export function createFieldDraftCoordinator(
  store: FieldDraftStore,
  save: (draft: FieldDraft) => Promise<FieldDraftOutcome>,
  transport: (event: FieldDraftEvent) => void,
) {
  const inFlight = new Set<string>();
  return (command: FieldDraftCommand) => {
    if (command.type === 'edit' || command.type === 'replace') {
      if (store.merge(command.draft, command.type === 'replace'))
        transport({ type: 'field-draft-sync', draft: store.get(command.draft)! });
      return;
    }
    const draft = store.commandTarget(command.draft);
    if (!draft) {
      // A reopened owner may not hold a previous view's draft. The originating
      // editor already marked this exact submission 'saving': silence would
      // strand it there forever, including during close permission checks.
      if (command.type === 'submit')
        transport({
          type: 'field-draft-result',
          draft: command.draft,
          outcome: {
            status: 'refused',
            reason:
              'This editor changed since you began typing. Check the current value before saving yours.',
          },
        });
      return;
    }
    if (command.type === 'discard') {
      if (draft.state === 'saving') return;
      store.remove(draft);
      transport({
        type: 'field-draft-discarded',
        draft: {
          id: draft.id,
          revision: draft.revision,
          documentId: draft.documentId,
          fieldId: draft.fieldId,
        },
      });
      return;
    }
    const requestId = `${draft.id}:${draft.revision}`;
    if (draft.state === 'refused' || inFlight.has(requestId)) return;
    inFlight.add(requestId);
    store.merge({ ...draft, state: 'saving' });
    transport({ type: 'field-draft-sync', draft: { ...draft, state: 'saving' } });
    void save(draft)
      .catch((): FieldDraftOutcome => ({
        status: 'refused',
        reason: 'Not saved. Your typing is kept; check the current value before trying again.',
      }))
      .then((outcome) => {
        store.finish(draft, outcome);
        transport({ type: 'field-draft-result', draft, outcome });
      })
      .finally(() => inFlight.delete(requestId));
  };
}

/**
 * Whether open typing has to ask the person which value to keep. A refused save always does, and
 * so does a changed field contract. A newer revision of the document only does when it changed
 * this field: a re-read that landed while they typed (a page turn's answer, applied in the
 * background) and left the field holding the value the typing began from changes nothing the
 * typing rests on, the same rule fieldDraftStillApplies uses to let that typing save.
 */
export function fieldDraftConflicted(
  draft: FieldDraft | undefined,
  binding: Pick<FieldDraftBinding, 'documentMutationToken' | 'fieldContractToken'>,
  currentValue: string,
) {
  if (!draft || draft.state === 'saving') return false;
  if (draft.state === 'refused' || draft.fieldContractToken !== binding.fieldContractToken)
    return true;
  return (
    draft.documentMutationToken !== binding.documentMutationToken &&
    draft.baseValue !== currentValue
  );
}

/** Navigation is explicit; disappearing/deleted workspaces never initiate writes. */
export function submitLeavingFieldDrafts(
  store: FieldDraftStore,
  previousId: string | null | undefined,
  nextId: string | null | undefined,
  previousStillExists: boolean,
) {
  if (!previousId || !nextId || previousId === nextId || !previousStillExists) return;
  for (const draft of store.getSnapshot()) if (draft.documentId === previousId) store.submit(draft);
}
